import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { CheckCircle, Search, Upload } from 'lucide-react';
import ApercuRapport from './ApercuRapport';

/**
 * « À quoi ressemblerait mon analyse ? » — l'exemple de rapport, sorti du flux.
 *
 * Il occupait 3 écrans sur mobile en plein milieu de la landing, entre le visiteur
 * et le formulaire. Il devient consultable à la demande : ceux qui hésitent
 * l'ouvrent, les autres vont droit au dépôt.
 *
 * Le déroulé en 3 étapes ouvre la modale — on explique le fonctionnement avant de
 * montrer le résultat. Il vient de l'ancienne section « Comment ça marche » de la
 * landing, retirée elle aussi.
 */

const ETAPES = [
  {
    icon: Upload,
    titre: 'Déposez vos documents',
    texte: "La lettre de recouvrement et toutes les pièces que vous avez — PDF, photo ou scan.",
  },
  {
    icon: Search,
    titre: "L'IA analyse votre dossier",
    texte: 'Prescription, conformité légale, délais, pièces manquantes : tout est vérifié.',
  },
  {
    icon: CheckCircle,
    titre: 'Découvrez vos chances',
    texte: 'Score gratuit et diagnostic personnalisé, en trois minutes.',
  },
];

const ExempleAnalyseModal = ({ open, onOpenChange, onCTAClick }) => {
  // Le CTA payant de l'exemple doit d'abord fermer la modale : sinon il fait défiler
  // la page vers un formulaire caché derrière le voile.
  //
  // Radix maintient un verrou de défilement sur <body> tant que l'animation de
  // fermeture tourne (duration-200 dans ui/dialog.jsx). Un scrollIntoView lancé
  // pendant ce verrou est purement et simplement avalé — constaté au navigateur.
  // On attend donc que le verrou soit levé plutôt que de parier sur un délai fixe,
  // avec une sortie de secours si la levée n'arrive jamais.
  const handleCTA = () => {
    onOpenChange(false);
    // Sondage par setTimeout et non par requestAnimationFrame : rAF ne se déclenche
    // pas dans un onglet en arrière-plan, la sortie de secours ne partirait jamais.
    const debut = Date.now();
    const quandDeverrouille = () => {
      if (document.body.style.overflow !== 'hidden' || Date.now() - debut > 800) {
        onCTAClick?.();
        return;
      }
      setTimeout(quandDeverrouille, 32);
    };
    setTimeout(quandDeverrouille, 32);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] w-[calc(100vw-24px)] max-w-[900px] overflow-y-auto p-0 sm:w-full">
        <div className="px-5 pt-5 sm:px-8 sm:pt-8">
          <DialogHeader>
            <DialogTitle
              className="text-left text-[22px] font-extrabold leading-tight tracking-tight sm:text-[30px]"
              style={{ color: '#1e3a8a' }}
            >
              Voilà exactement ce que vous recevez
            </DialogTitle>
            <DialogDescription className="text-left text-[14px] leading-relaxed sm:text-[16px]" style={{ color: '#475569' }}>
              Un vrai rapport, pas un aperçu. Le diagnostic complet est gratuit — le plan d'action
              juridique est optionnel.
            </DialogDescription>
          </DialogHeader>

          {/* Le déroulé, avant l'exemple */}
          <div className="mt-6 grid gap-3 sm:grid-cols-3 sm:gap-4">
            {ETAPES.map(({ icon: Icon, titre, texte }, i) => (
              <div
                key={titre}
                className="flex gap-3 rounded-xl border p-4 sm:flex-col sm:gap-2"
                style={{ borderColor: '#e2e8f0', backgroundColor: '#f8fafc' }}
              >
                <div className="flex items-start gap-2 sm:items-center">
                  <div
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[12px] font-bold text-white"
                    style={{ backgroundColor: '#1e3a8a' }}
                  >
                    {i + 1}
                  </div>
                  <Icon size={18} className="hidden shrink-0 sm:block" style={{ color: '#1e3a8a' }} />
                </div>
                <div>
                  <div className="text-[14px] font-semibold" style={{ color: '#1e3a8a' }}>{titre}</div>
                  <div className="mt-[2px] text-[12.5px] leading-snug" style={{ color: '#64748b' }}>{texte}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="px-5 pb-6 pt-6 sm:px-8 sm:pb-8">
          <ApercuRapport onCTAClick={handleCTA} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExempleAnalyseModal;
